import { useState, useEffect, useRef } from 'react';

const TIPS = [
    {
        id: 1,
        target: '.application-areas',
        title: 'Choose Your Space',
        message: 'Select where you want to visualize the stone - Kitchen, Vanity, or Wall Cladding',
        position: 'bottom'
    },
    {
        id: 2,
        target: '.sub-nav',
        title: 'Browse Materials',
        message: 'Choose a material brand like Dekton, Caesarstone, or Granite to explore',
        position: 'bottom'
    },
    {
        id: 3,
        target: '.samples-grid .sample-item',
        title: 'Preview Samples',
        message: 'Click any sample to see how it looks in your selected space',
        position: 'top'
    },
    {
        id: 4,
        target: '.navigation-controls',
        title: 'Explore Styles',
        message: 'Use the arrows to browse different room styles and layouts',
        position: 'bottom'
    },
    {
        id: 5,
        target: '.liked-badge-btn',
        title: 'Save Favorites',
        message: 'Click the heart to save materials you love to your favorites list.',
        position: 'top'
    },
    {
        id: 6,
        target: '.compare-badge-btn',
        title: 'Compare Colors',
        message: 'Add two materials to compare them side-by-side with an interactive slider!',
        position: 'top'
    },
    {
        id: 7,
        target: '.cabinet-2t-swatch, .cabinet-swatches-grid',
        title: '2 Tone Cabinets',
        message: 'Mix cabinet colors! Select "2 Tone" to independently pick upper and lower cabinet colors.',
        position: 'top'
    },
    {
        id: 8,
        target: '.request-sample-toggle-btn, .mobile-bottom-cta',
        title: 'Request Samples',
        message: 'Love what you see? Request physical samples to feel the real texture!',
        position: 'top'
    }
];

const STORAGE_KEY = 'visualizer_onboarding_dismissed';

const OnboardingTips = ({ onComplete }) => {
    const [currentStep, setCurrentStep] = useState(0);
    const [isVisible, setIsVisible] = useState(true);
    const [tooltipPosition, setTooltipPosition] = useState({ top: 0, left: 0 });
    const [arrowPosition, setArrowPosition] = useState('bottom');
    const [dontShowAgain, setDontShowAgain] = useState(false);
    const tooltipRef = useRef(null);

    // Check if user has dismissed onboarding before
    useEffect(() => {
        const dismissed = localStorage.getItem(STORAGE_KEY);
        if (dismissed === 'true') {
            setIsVisible(false);
            if (onComplete) onComplete();
        }
    }, [onComplete]);

    // Position tooltip relative to target element
    useEffect(() => {
        if (!isVisible || currentStep >= TIPS.length) return;

        const tip = TIPS[currentStep];
        const targetSelector = tip.target.split(', ')[0]; // Use first selector
        const targetElement = document.querySelector(targetSelector);

        if (targetElement) {
            const rect = targetElement.getBoundingClientRect();
            const tooltipHeight = 180;
            const tooltipWidth = 300;
            const padding = 15;

            let top, left;
            let position = tip.position;

            // Calculate position based on preferred position
            if (position === 'bottom') {
                top = rect.bottom + padding;
                left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
            } else {
                top = rect.top - tooltipHeight - padding;
                left = rect.left + (rect.width / 2) - (tooltipWidth / 2);
            }

            // Adjust if going off-screen
            if (top < 10) {
                top = rect.bottom + padding;
                position = 'bottom';
            }
            if (top + tooltipHeight > window.innerHeight - 10) {
                top = rect.top - tooltipHeight - padding;
                position = 'top';
            }
            if (left < 10) left = 10;
            if (left + tooltipWidth > window.innerWidth - 10) {
                left = window.innerWidth - tooltipWidth - 10;
            }

            setTooltipPosition({ top, left });
            setArrowPosition(position);

            // Highlight target element
            targetElement.classList.add('onboarding-highlight');

            return () => {
                targetElement.classList.remove('onboarding-highlight');
            };
        }
    }, [currentStep, isVisible]);

    const handleNext = () => {
        // Remove highlight from current target
        const currentTip = TIPS[currentStep];
        const currentTarget = document.querySelector(currentTip.target.split(', ')[0]);
        if (currentTarget) currentTarget.classList.remove('onboarding-highlight');

        if (currentStep < TIPS.length - 1) {
            setCurrentStep(currentStep + 1);
        } else {
            handleComplete();
        }
    };

    const handleSkip = () => {
        handleComplete();
    };

    const handleComplete = () => {
        // Remove all highlights
        TIPS.forEach(tip => {
            const target = document.querySelector(tip.target.split(', ')[0]);
            if (target) target.classList.remove('onboarding-highlight');
        });

        if (dontShowAgain) {
            localStorage.setItem(STORAGE_KEY, 'true');
        }
        setIsVisible(false);
        if (onComplete) onComplete();
    };

    if (!isVisible || currentStep >= TIPS.length) return null;

    const tip = TIPS[currentStep];

    return (
        <>
            {/* Overlay */}
            <div className="onboarding-overlay" onClick={handleSkip} />

            {/* Tooltip */}
            <div
                ref={tooltipRef}
                className={`onboarding-tooltip arrow-${arrowPosition}`}
                style={{ top: tooltipPosition.top, left: tooltipPosition.left }}
            >
                <div className="onboarding-header">
                    <span className="onboarding-step">💡 Step {currentStep + 1} of {TIPS.length}</span>
                </div>

                <h3 className="onboarding-title">{tip.title}</h3>
                <p className="onboarding-message">{tip.message}</p>

                <div className="onboarding-actions">
                    <button className="onboarding-skip" onClick={handleSkip}>
                        Skip
                    </button>
                    <button className="onboarding-next" onClick={handleNext}>
                        {currentStep === TIPS.length - 1 ? 'Got it!' : 'Next →'}
                    </button>
                </div>

                <label className="onboarding-dismiss">
                    <input
                        type="checkbox"
                        checked={dontShowAgain}
                        onChange={(e) => setDontShowAgain(e.target.checked)}
                    />
                    Don't show again
                </label>
            </div>
        </>
    );
};

export default OnboardingTips;
